import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier, localizedStorefrontURL } from '@theme/utilities';


// Native A remains usable when allocation cannot initialize (for example an
// unavailable crypto API). This fallback still requires the exact product and
// never imports the other arm's form into the current section.
function materializeNativePriceFallback(html, productId) {
  const templates = [...html.querySelectorAll('template[data-sf-price-arm]')];
  const target = templates.find(tpl => tpl.dataset.sfPriceProduct === productId
    && tpl.content.querySelector('variant-picker[data-product-id="' + productId + '"]')
    && tpl.content.querySelector('product-form-component[data-product-id="' + productId + '"]'));
  if (!target) return false;
  target.closest('.shopify-section')?.setAttribute('data-sf-price-active', target.dataset.sfPriceArm);
  const fragment = html.importNode(target.content, true);
  fragment.querySelectorAll('variant-picker').forEach(picker => { picker.dataset.templateProductMatch = 'true'; });
  target.replaceWith(fragment);
  templates.filter(tpl => tpl !== target).forEach(tpl => tpl.closest('.shopify-section')?.remove());
  html.querySelectorAll('script[data-sf-price-mount], noscript').forEach(node => node.remove());
  return true;
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #onChange = (event) => this.variantChanged(event);

  #cacheOptionInputs() {
    const fields = this.refs.fieldsets || [];
    this.#radios = fields.map(field => Array.from(field?.querySelectorAll('input') || []));
    this.#checkedIndices = this.#radios.map(radios => {
      let index = radios.findIndex(radio => radio.dataset.currentChecked === 'true');
      if (index < 0) index = radios.findIndex(radio => radio.checked);
      return index < 0 ? [] : [index];
    });
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.closest('[data-sf-price-active]')) this.dataset.templateProductMatch = 'true';

    this.#cacheOptionInputs();
    this.addEventListener('change', this.#onChange);
    this.#resizeObserver.observe(this);
    const restore = () => this.#restorePriceVariant();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restore, { once: true });
    else queueMicrotask(restore);
  }

  // Only crossed/old deep links need an initial variant request. Ordinary price
  // test arrivals already have the right native product and make no request.
  #restorePriceVariant() {
    const section = this.closest('[data-sf-price-active]');
    const wanted = section?.dataset.sfPriceRestore;
    if (!wanted || section.dataset.sfPriceRestoreStarted === 'true') return;
    section.dataset.sfPriceRestoreStarted = 'true';
    const choice = window.SofthourPriceAB?.variants?.find(item => String(item.id) === wanted);
    if (choice) {
      [choice.color, choice.size].forEach((value, index) => {
        const radio = [...(this.refs.fieldsets?.[index]?.querySelectorAll('input') || [])].find(input => input.value === value);
        if (radio) this.updateSelectedOption(radio);
      });
    }
    this.dispatchEvent(new VariantSelectedEvent({ id: this.selectedOptionId || '' }));
    const url = new URL(localizedStorefrontURL(this.dataset.productUrl), window.location.origin);
    url.searchParams.set('variant', wanted);
    const pageUrl = new URL(window.location.href);
    for (const key of ['country', 'language', 'preview_theme_id', 'sf_preview']) {
      if (pageUrl.searchParams.has(key)) url.searchParams.set(key, pageUrl.searchParams.get(key));
    }
    window.SofthourPriceAB?.note('variant_restore', 'requesting');
    this.fetchUpdatedSection(url.toString(), undefined, wanted);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('change', this.#onChange);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = localizedStorefrontURL(this.dataset.productUrl || window.location.pathname).split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl
      ? localizedStorefrontURL(selectedOption.dataset.connectedProductUrl)
      : undefined;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = new URL(newUrl, window.location.origin).pathname;
    } else if (isOnProductPage && this.closest('[data-sf-price-active]')) {
      // No navigation: retain every attribution parameter but make reloads use
      // the product that actually owns the selected native variant.
      url.pathname = new URL(currentUrl, window.location.origin).pathname;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');
      // Native morph replaces children while preserving this custom element.
      // Refresh stale references before touching the selected pill.
      if (this.#radios[fieldsetIndex]?.[inputIndex] !== target) this.#cacheOptionInputs();

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
      if (this.dataset.productId === '10195007799351') {
        const label = target.closest('fieldset')?.querySelector('legend .variant-option__swatch-value');
        if (label) label.textContent = target.value;
      }
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = localizedStorefrontURL(
      selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl || window.location.pathname
    );
    this.#pendingRequestUrl = productUrl;
    const params = new URLSearchParams();
    // Shopify draft links need their preview key when native option sections are requested.
    const pageUrl = new URL(window.location.href);
    if (pageUrl.pathname.endsWith('/products_preview') && pageUrl.searchParams.has('preview_key')) {
      productUrl = pageUrl.pathname;
      params.set('preview_key', pageUrl.searchParams.get('preview_key'));
    }
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.set('view', viewParamValue);
    for (const key of ['country', 'language', 'preview_theme_id', 'sf_preview']) {
      if (pageUrl.searchParams.has(key)) params.set(key, pageUrl.searchParams.get(key));
    }

    if (this.selectedOptionsValues.length && !source) {
      params.set('option_values', this.selectedOptionsValues.join(','));
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.set('option_values', sourceSelectedOptionsValues.join(','));
      } else {
        params.set('option_values', selectedOption.dataset.optionValueId);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    const requestUrl = new URL(productUrl, pageUrl.origin);
    requestUrl.searchParams.delete('variant');
    requestUrl.searchParams.delete('option_values');
    requestUrl.searchParams.delete('section_id');
    if (closestSectionId) params.set('section_id', SECTION_ID_MAP[closestSectionId]);
    else if (this.dataset.productId === '10195007799351' && this.dataset.templateProductMatch === 'true' && !this.closest('product-card, quick-add-dialog')) {
      // The native product section contains picker, form, price and gallery.
      // Fetch that section instead of the full storefront document per click.
      const sectionId = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || this.dataset.sectionId;
      if (sectionId) params.set('section_id', sectionId);
    }
    for (const [key, value] of params) requestUrl.searchParams.set(key, value);
    return localizedStorefrontURL(requestUrl.pathname + requestUrl.search);
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, expectedVariantId) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    return fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Native variant could not be loaded');
        return response.text();
      })
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // A/B HTML is inert until the same native product is selected. This
        // runs before every selector used by picker, form, price and gallery.
        // Never select from the other arm, even if it appears first in HTML.
        if (html.querySelector('template[data-sf-price-arm]')) {
          const materialize = window.SofthourPriceAB?.materialize;
          const restored = materialize
            ? materialize(html, this.dataset.productId)
            : materializeNativePriceFallback(html, this.dataset.productId);
          if (!restored) {
            throw new Error('Price test product response missing');
          }
        }

        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        window.SofthourStockAvailability?.apply(html);

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) throw new Error('Native variant response missing');
        if (expectedVariantId && String(JSON.parse(textContent)?.id || '') !== String(expectedVariantId)) {
          throw new Error('Requested price-test variant was not restored');
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the URL from the fetched variant, since option data can be stale during rapid changes.
        if (this.dataset.templateProductMatch === 'true' && !this.closest('product-card, quick-add-dialog')) {
          const resolvedVariant = JSON.parse(textContent);
          const resolvedUrl = new URL(window.location.href);
          if (this.closest('[data-sf-price-active]')) {
            resolvedUrl.pathname = new URL(localizedStorefrontURL(this.dataset.productUrl), window.location.origin).pathname;
          }
          if (resolvedVariant?.id) resolvedUrl.searchParams.set('variant', resolvedVariant.id);
          else resolvedUrl.searchParams.delete('variant');
          history.replaceState({}, '', resolvedUrl.toString());
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
        const priceSection = this.closest('[data-sf-price-active]');
        if (priceSection?.dataset.sfPriceRestore) {
          delete priceSection.dataset.sfPriceRestore;
          delete priceSection.dataset.sfPriceRestoreStarted;
          priceSection.removeAttribute('aria-busy');
          priceSection.querySelector('[data-sf-price-restore-error]')?.remove();
          window.SofthourPriceAB?.note('variant_restore', 'confirmed');
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
          // A failed section request must cancel queued purchase intent rather
          // than leave the form waiting forever or add a stale native variant.
          this.dispatchEvent(new CustomEvent('variant:error', { bubbles: true, detail: { productId: this.dataset.productId } }));
          const priceSection = this.closest('[data-sf-price-active]');
          if (priceSection?.dataset.sfPriceRestore) {
            window.SofthourPriceAB?.note('variant_restore', 'failed');
            window.SofthourPriceAB?.track?.('render_error');
            if (!priceSection.querySelector('[data-sf-price-restore-error]')) {
              const notice = document.createElement('p');
              notice.dataset.sfPriceRestoreError = '';
              notice.setAttribute('role', 'status');
              notice.textContent = 'Your previous selection could not be loaded. Please choose your color and size again.';
              this.after(notice);
            }
          }
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl
        ? localizedStorefrontURL(newVariantPickerSource.dataset.productUrl)
        : undefined;
      if (newProductUrl) newVariantPickerSource.dataset.productUrl = newProductUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#cacheOptionInputs();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
